import type { SessionUser } from "types/session-user";

type OneTimePurchaseData = {
  isLoading: boolean;
  totalLicenses: number;
  availableLicenses: number;
  otpProjectsCapacity: number;
  otpProjectsUsed: number;
  hasAvailableTrial: boolean;
  hasActiveTrial: boolean;
  trialDaysRemaining: number;
  trialExpiryDate: Date | string | null;
  canCreateProject: boolean;
  hasAccess: boolean;
};

const getUnknownField = (obj: unknown, key: string): unknown => {
  if (!obj || typeof obj !== "object") return undefined;
  return (obj as Record<string, unknown>)[key];
};

export const selectOneTimePurchaseData = (
  session: SessionUser | null,
  unlicensedUnderCap = false
): OneTimePurchaseData => {
  if (!session) {
    return {
      isLoading: true,
      totalLicenses: 0,
      availableLicenses: 0,
      otpProjectsCapacity: 0,
      otpProjectsUsed: 0,
      hasAvailableTrial: false,
      hasActiveTrial: false,
      trialDaysRemaining: 0,
      trialExpiryDate: null,
      canCreateProject: false,
      hasAccess: false,
    };
  }

  const isOneTimePurchase =
    session.billing_model === "one_time" || Boolean(session.is_one_time_purchase);
  const one_time_purchases = session.one_time_purchases;
  const otp = one_time_purchases ?? {
    total_licenses: 0,
    available_licenses: 0,
    latest_purchase_date: null,
    has_available_trial: false,
    has_active_trial: false,
    trial_days_remaining: 0,
    trial_expiry_date: null,
  };

  const usedLicensesRaw = getUnknownField(otp, "used_licenses");
  const usedLicenses =
    typeof usedLicensesRaw === "number"
      ? usedLicensesRaw
      : otp.total_licenses - otp.available_licenses;

  const otpCapacity = otp.total_licenses;
  const otpUsed = Math.max(0, usedLicenses);

  const trialDaysRemaining = otp.trial_days_remaining ?? 0;
  const trialExpiryDate = otp.trial_expiry_date ?? null;

  const hasLicenseAccess = otp.available_licenses > 0;
  const hasTrialAccess = otp.has_active_trial && trialDaysRemaining > 0;

  return {
    isLoading: false,
    totalLicenses: otp.total_licenses,
    availableLicenses: otp.available_licenses,
    otpProjectsCapacity: otpCapacity,
    otpProjectsUsed: otpUsed,
    hasAvailableTrial: otp.has_available_trial,
    hasActiveTrial: otp.has_active_trial,
    trialDaysRemaining,
    trialExpiryDate,
    canCreateProject:
      Boolean(isOneTimePurchase) && session.is_active && (hasLicenseAccess || unlicensedUnderCap),
    hasAccess: hasLicenseAccess || hasTrialAccess,
  };
};
